import { Component, OnInit, inject } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { FormsModule, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';

export interface ProjectCopyDialogData {
  projectId: string;
  projectName?: string;
}

export interface ProjectCopyResult {
  success: boolean;
  targetProjectId?: string;
  copySharingSettings?: boolean;
  error?: string;
}

/** Dialog for duplicating a project: lets the user specify a target project name and choose whether to copy sharing settings. */
@Component({
  selector: 'app-project-copy-dialog',
  templateUrl: './project-copy-dialog.component.html',
  styleUrls: ['./project-copy-dialog.component.scss'],
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatProgressBarModule,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class ProjectCopyDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ProjectCopyDialogComponent>>(MatDialogRef);
  data = inject<ProjectCopyDialogData>(MAT_DIALOG_DATA);

  targetProjectId: string = '';
  copySharingSettings: boolean = false;
  loading = false;
  error: string | null = null;

  // Reactive form control for project ID validation
  projectIdFormControl = new FormControl('', {
    validators: [
      Validators.required,
      Validators.pattern(/^[a-zA-Z0-9._-]+$/), // Allow alphanumeric, dots, underscores, hyphens
      Validators.minLength(3),
      Validators.maxLength(50),
      this.projectIdNotSameValidator.bind(this),
    ],
  });

  ngOnInit() {
    // Set default target project ID to source project ID + "-copy"
    this.targetProjectId = `${this.data.projectId}-copy`;
    this.projectIdFormControl.setValue(this.targetProjectId);
  }

  /**
   * Custom validator to ensure target project ID is not the same as source project ID
   */
  projectIdNotSameValidator(control: any) {
    if (control.value === this.data.projectId) {
      return { sameAsSource: true };
    }
    return null;
  }

  /**
   * Get error message for project ID validation
   */
  getProjectIdErrorMessage(): string {
    if (this.projectIdFormControl.hasError('required')) {
      return 'Project ID is required';
    }
    if (this.projectIdFormControl.hasError('pattern')) {
      return 'Project ID can only contain letters, numbers, dots, underscores, and hyphens';
    }
    if (this.projectIdFormControl.hasError('minlength')) {
      return 'Project ID must be at least 3 characters long';
    }
    if (this.projectIdFormControl.hasError('maxlength')) {
      return 'Project ID must be no more than 50 characters long';
    }
    if (this.projectIdFormControl.hasError('sameAsSource')) {
      return 'Target project ID must be different from source project ID';
    }
    return '';
  }

  /**
   * Check if the form is valid and ready to submit
   */
  isFormValid(): boolean {
    return this.projectIdFormControl.valid && this.targetProjectId.trim().length > 0;
  }

  /**
   * Update the form control when the input changes
   */
  onProjectIdChange() {
    this.projectIdFormControl.setValue(this.targetProjectId);
    this.projectIdFormControl.markAsTouched();
  }

  /**
   * Handle the cancel action
   */
  onCancel(): void {
    if (!this.loading) {
      this.dialogRef.close();
    }
  }

  /**
   * Handle the copy action
   */
  onCopy(): void {
    if (!this.isFormValid() || this.loading) {
      return;
    }

    const result: ProjectCopyResult = {
      success: true,
      targetProjectId: this.targetProjectId.trim(),
      copySharingSettings: this.copySharingSettings,
    };

    this.dialogRef.close(result);
  }

  /**
   * Clear any error messages
   */
  clearError(): void {
    this.error = null;
  }
}
